/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Observable, Subject } from 'rxjs';
import { CoreErrorKey } from '../key/core-error.key';
import { ApplicationError } from '../model/application-error.model';
import { Locale } from '../model/locale.model';

export type LocaleResolver = () => Promise<Locale>;

export class LocaleChangedEvent {
  constructor(
    public readonly localeKey: string,
    public readonly packageKey: string,
    public readonly locale: Locale | LocaleResolver
  ) { }
}

export class LocaleProvider {
  private static readonly localeMap = new Map<string, { [packageKey: string]: Locale | LocaleResolver }>();
  private static readonly localeChangedSubject = new Subject<any>();

  public static get localeChanged(): Observable<any> {
    return LocaleProvider.localeChangedSubject.asObservable();
  }

  public static registerPackageLocale(localeKey: string, packageKey: string, locale: Locale | LocaleResolver) {
    let packageLocales = LocaleProvider.localeMap.get(localeKey);
    if (!packageLocales) {
      LocaleProvider.localeMap.set(localeKey, packageLocales = {});
    } else if (packageLocales[packageKey] === locale) {
      return;
    }
    packageLocales[packageKey] = locale;
    LocaleProvider.localeChangedSubject.next(new LocaleChangedEvent(localeKey, packageKey, locale));
  }

  public static async resolvePackageLocale(localeKey: string, packageKey: string): Promise<Locale> {
    const packageLocales = LocaleProvider.localeMap.get(localeKey);
    if (!packageLocales) {
      return undefined;
    }
    const packageLocale = packageLocales[packageKey];
    try {
      return typeof packageLocale === 'function' ? packageLocale() : packageLocale;
    } catch (error) {
      throw new ApplicationError({
        key: CoreErrorKey.LOCALE_RESOLVE_ERROR,
        arguments: { localeKey, packageKey, error }
      });
    }
  }

  public static async resolvePackageLocales(localeKey: string): Promise<{ [packageKey: string]: Locale }> {
    const packageLocales = LocaleProvider.localeMap.get(localeKey);
    if (!packageLocales) {
      return undefined;
    }
    await Promise.all(Object.keys(packageLocales).map(async (packageKey: string) => {
      const packageLocale = packageLocales[packageKey];
      if (typeof packageLocale === 'function') {
        try {
          packageLocales[packageKey] = await packageLocale();
        } catch (error) {
          throw new ApplicationError({
            key: CoreErrorKey.LOCALE_RESOLVE_ERROR,
            arguments: { localeKey, packageKey, error }
          });
        }
      }
    }));
    return packageLocales as { [packageKey: string]: Locale };
  }
}
